// Renders the page's sections in the order set in the editor.
import React from "react";
import { Container, Row, Col } from "react-bootstrap";
import Bio from "./Bio";
import SectionHeader from "./SectionHeader";
import MusicSamples from "./MusicSamples";
import Gallery from "./Gallery";
import BandMembers from "./BandMembers";
import ShowsSection from "./Shows";
import { useImageSrc, useSite } from "./content/context";
import { Section, SECTION_TYPE_INFO } from "./content/layout";
import { EditableText } from "./edit/primitives";
import { AddSectionButton, SectionFrame } from "./sections/SectionFrame";
import { ButtonsSection, PhotosSection, PhotoTextSection, TextSection, VideoSection } from "./sections/CustomSections";
import { newsletterDisplay, NewsletterSection } from "./sections/Newsletter";
import { kitFormAction } from "./sections/kit";
import { sectionBoxStyle } from "./theme/theme";
import { useSections } from "./sections/useSections";
import { sectionAnchor } from "./SiteHeader";

// Only render a plain address, so the mailto: link can't be turned into anything else.
const EMAIL = /^[^\s@<>"]+@[^\s@<>"]+\.[^\s@<>"]+$/;
export const isEmail = (value: string) => EMAIL.test(value);

function ContactSection() {
  const { data, edit } = useSite();
  const raw = data.site.contactEmail?.trim() ?? "";
  const contactEmail = isEmail(raw) ? raw : undefined;
  return (
    <Row className="text-center">
      <Col>
        <p>
          Email:{" "}
          <EditableText
            label="Contact email"
            value={raw}
            onChange={(value) => edit?.update("site", { ...data.site, contactEmail: value })}
            validate={(v) => (!v || isEmail(v) ? undefined : "That doesn't look like an email address.")}
          />
        </p>
        {contactEmail ? (
          <a className="btn af-button" href={`mailto:${contactEmail}`}>
            Book Us
          </a>
        ) : null}
      </Col>
    </Row>
  );
}

function SectionContent({ section, update }: { section: Section; update: (patch: Partial<Section>) => void }) {
  switch (section.type) {
    case "bio":
      return <Bio />;
    case "music":
      return <MusicSamples />;
    case "shows":
      return <ShowsSection />;
    case "members":
      return <BandMembers />;
    case "gallery":
      return <Gallery />;
    case "contact":
      return <ContactSection />;
    case "text":
      return <TextSection section={section} update={update} />;
    case "photoText":
      return <PhotoTextSection section={section} update={update} />;
    case "video":
      return <VideoSection section={section} update={update} />;
    case "photos":
      return <PhotosSection section={section} update={update} />;
    case "buttons":
      return <ButtonsSection section={section} update={update} />;
    case "newsletter":
      return <NewsletterSection section={section} update={update} />;
    default:
      return null;
  }
}

// Bio and Shows have their own headings; every other section gets an editable one.
const HAS_HEADER = (section: Section) => section.type !== "bio" && section.type !== "shows";

const EPK: React.FC = () => {
  const { data, edit } = useSite();
  const imageSrc = useImageSrc();
  const { sections, updateSection } = useSections();

  // Contact disappears from the public page if there's no valid email.
  const contactEmail = data.site.contactEmail?.trim() ?? "";
  const onPublicPage = (s: Section) => {
    if (s.hidden) return false;
    if (s.type === "contact") return isEmail(contactEmail);
    // A pop-up-only (or unconnected) newsletter has nothing to show on the page itself.
    if (s.type === "newsletter") return Boolean(kitFormAction(s.formId)) && newsletterDisplay(s) !== "popup";
    return true;
  };
  const visible = sections.filter((s) => edit || onPublicPage(s));

  const box = (section: Section, children: React.ReactNode) =>
    section.style ? (
      <div
        className={`af-section-box${section.style.width === "boxed" ? " af-boxed" : ""}`}
        style={sectionBoxStyle(section.style, data.layout.theme, imageSrc(section.style.backgroundImage))}
      >
        {children}
      </div>
    ) : (
      children
    );

  return (
    <Container fluid className="px-0 af-content">
      <AddSectionButton index={0} />
      {visible.map((section) => {
        const index = sections.indexOf(section);
        const update = (patch: Partial<Section>) => updateSection(section.id, patch);
        const title = section.title ?? SECTION_TYPE_INFO[section.type].defaultTitle ?? "";
        return (
          <React.Fragment key={section.id}>
            <div id={sectionAnchor(section)} className="af-anchor" />
            <SectionFrame section={section} index={index}>
              {box(
                section,
                <>
                  {HAS_HEADER(section) && (title || edit) ? <SectionHeader title={title} onChange={(t) => update({ title: t })} /> : null}
                  <SectionContent section={section} update={update} />
                </>,
              )}
            </SectionFrame>
            <AddSectionButton index={index + 1} />
          </React.Fragment>
        );
      })}
    </Container>
  );
};

export default EPK;
